import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../components/forms/Buttons/Button';
import Select from '../../../../components/forms/Select/Select';
import {
  getApplication,
  patchApplication,
} from '../../../../services/application/application';
import useSnackbar from '../../../../utils/hooks/useSnackbar';
import { showImageOrPdf } from '../../../../utils/helpers/helpers';
import {
  deleteSocialDocument,
  getSocialDocuments,
  postSocialDocument,
} from '../../../../services/socialDocs/socialDocs';
import { ArrowUpTrayIcon, TrashIcon } from '@heroicons/react/24/solid';
import { getSocialDocumentsTypes } from '../../../../services/socialDocs/socialDocsTypes';
import {
  renderRule,
  countMinRequired,
} from '../../../../utils/helpers/vulnrableDocs';

// Schools whose ASP status and social documents school admins may change:
// 'ANY' = every school, ['091'] = only these, [] = none.
// The backend has the same switch: ASP_ENABLED_SCHOOLS in core/views/application.py.
// const ASP_ENABLED_SCHOOLS = ['091'];
const ASP_ENABLED_SCHOOLS = 'ANY';

const isASPDisabled = (schoolId) => {
  if (ASP_ENABLED_SCHOOLS === 'ANY') return false;
  return !ASP_ENABLED_SCHOOLS.includes(schoolId);
};

export default function ASPTab({ student, refreshData }) {
  const [i18n] = useTranslation();
  const [t] = useTranslation();
  const { addSnackbar } = useSnackbar();

  const [vulnerableGroups, setVulnerableGroups] = useState([]);
  const [fileList, setFileList] = useState(null);
  const [aspLoading, setAspLoading] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [isAsp, setIsAsp] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [uploadDisabled, setUploadDisabled] = useState(false);

  useEffect(() => {
    if (student?.uuid) {
      getApplication(student?.uuid).then((res) => {
        setIsAsp(res?.social_doc_type ? true : false);
        setSelectedGroup(res?.social_doc_type);
      });

      getSocialDocumentsTypes().then((res) => {
        setVulnerableGroups(res.results);
      });

      getSocialDocuments({ application: student.uuid }).then((res) => {
        setUploadedDocuments(res.results);
      });
    }

    return () => {};
  }, [student]);

  const handleFileChange = (e) => {
    setFileList(e.target.files);
  };

  const updateAspStatus = async (socDocs, categoryFiles) => {
    let body;

    if (
      socDocs.length >= countMinRequired(categoryFiles) &&
      socDocs.length !== 0
    ) {
      body = {
        asp_status: 2,
      };
    } else {
      body = {
        asp_status: 1,
      };
    }
    await patchApplication(student?.uuid, body).then(() => {
      addSnackbar(t('Успешно'), 'success');
    });
    //   .finally(() => {
    //     setLoading(false);
    //   });
  };

  const handleUploadClick = async () => {
    setAspLoading(true);
    if (!fileList || !selectedGroup.uuid) {
      return;
    }

    for (const file of files) {
      const data = new FormData();

      data.append('application', student?.uuid);
      data.append(`document`, file, file.name);

      try {
        await postSocialDocument(data).then((res) => {
          // console.log('res', res);
        });
      } catch (error) {
        addSnackbar(t('Ошибка'), 'error');
      }
    }
    try {
      await getSocialDocuments({ application: student?.uuid })
        .then((res) => {
          setUploadedDocuments(res.results);
          updateAspStatus(res?.results, selectedGroup?.files);
        })
        .finally(() => {
          setAspLoading(false);
          setFileList(null);
          setUploadDisabled(false);
        });
    } catch (error) {
      addSnackbar(t('Ошибка'), 'error');
    }
  };

  const files = fileList ? [...fileList] : [];

  return (
    <div className=''>
      <div className='p-4'>
        <h3 className='text-lg font-medium leading-6 text-gray-900'>
          Документы, подтверждающие принадлежность к социально уязвимым группам
        </h3>
      </div>
      <div className='flex cursor-pointer items-center border-y p-4'>
        <input
          id='checkRU'
          name='checkRU'
          type='checkbox'
          disabled={isASPDisabled(student?.school?.school_id)}
          checked={isAsp}
          value={isAsp}
          onChange={(e) => {
            setIsAsp((v) => !v);

            if (e.target.checked === false) {
              setUploadDisabled(true);
              const body = {
                social_doc_type: '',
                // asp_status: getAspStatus(),
              };
              patchApplication(student?.uuid, body)
                .then((res) => {
                  // setApplicantInfo(res);
                  refreshData();
                  setSelectedGroup(null);
                  addSnackbar(t('Категория изменена'), 'success');
                })
                .finally(() => {
                  setUploadDisabled(false);
                });
            }
          }}
          className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
        />
        <label htmlFor='checkRU' className={'ml-2 block'}>
          Относится ли ученик к социально уязвимым категориям населения
        </label>
      </div>
      <div
        className={`${
          isAsp ? '' : 'pointer-events-none opacity-50'
        } flex flex-col gap-4`}
      >
        <div className='mx-4 mt-2 flex flex-row items-center justify-between'>
          <h3 className='w-2/3'>
            <span className='text-lg font-semibold text-blue-600'>
              Категория:
            </span>
            {/* {applicant?.social_doc_type?.name} */}
            <Select
              loading={uploadDisabled}
              isSearchable={false}
              className=''
              value={selectedGroup?.uuid}
              options={[
                // { label: t('signup.not_in_category'), value: null },
                ...vulnerableGroups.map((group, i) => ({
                  label:
                    `${i + 1}. ` +
                    (i18n.language === 'kk' ? group?.name : group?.name_ru),
                  value: group?.uuid,
                  docs_list: { kz: group?.files, ru: group?.files_ru },
                  data: group,
                })),
              ]}
              // placeholder={t('announcement.select_positions')}
              // onChange={(e) => field.onChange(e.value)}
              onChange={(e) => {
                setSelectedGroup(e.data);
                setUploadDisabled(true);
                const body = {
                  social_doc_type: isAsp ? e.data?.uuid : '',
                  // asp_status: getAspStatus(),
                };
                patchApplication(student?.uuid, body)
                  .then((res) => {
                    // setApplicantInfo(res);
                    refreshData();
                    addSnackbar(t('Категория изменена'), 'success');
                  })
                  .finally(() => {
                    setUploadDisabled(false);
                  });
              }}
            />
          </h3>
          {/* <Button text='Добавить документ' Icon={PlusIcon} /> */}
        </div>
        <div className='mx-4'>
          <p className='font-semibold text-blue-600'>
            Список подтверждающих документов:
          </p>
          <div>
            <ul className='rounded-md border bg-white p-2'>
              {/* {selectedGroup?.files?.map((file, i) => (
                <li className='m-1' key={i}>
                  {`${i + 1}. ` + file}
                </li>
              ))} */}
              {!!selectedGroup &&
                renderRule(
                  i18n.language === 'kk'
                    ? selectedGroup?.files
                    : selectedGroup?.files_ru,
                  t,
                )}
            </ul>
          </div>
        </div>
        <div className='mx-4'>
          <p className='mb-2 font-semibold text-blue-600'>
            Загруженные документы:
          </p>
          <div className='grid min-h-[4rem] grid-cols-3 gap-2'>
            {uploadedDocuments?.map((doc) =>
              doc?.document ? (
                <div
                  key={doc?.uuid}
                  className='flex flex-col justify-between rounded-md border p-2'
                >
                  {/* <img
          src={doc?.document}
          alt='doc'
          className='h-32 object-cover'
        /> */}
                  {showImageOrPdf(doc?.document, '4')}
                  <Button
                    className='mt-2 w-16'
                    color='error'
                    Icon={TrashIcon}
                    onClick={() => {
                      setUploadDisabled(true);
                      deleteSocialDocument(doc?.uuid).then(() => {
                        getSocialDocuments({
                          application: student?.uuid,
                        })
                          .then((res) => {
                            setUploadedDocuments(res.results);
                            updateAspStatus(res?.results, selectedGroup?.files);
                          })
                          .finally(() => {
                            setUploadDisabled(false);
                          });
                      });
                    }}
                  />
                </div>
              ) : (
                <div></div>
              ),
            )}
          </div>
        </div>
        <div className='m-4 rounded-md border p-2 '>
          <p className='font-semibold text-blue-600'>Выберите документы:</p>
          <div>
            <input
              disabled={isASPDisabled(student?.school?.school_id)}
              type='file'
              onChange={handleFileChange}
              multiple
            />

            <ul className='mt-2 border-y bg-white p-2'>
              {files?.length ? (
                files.map((file, i) => (
                  <li className='m-1' key={i}>
                    {file.name} - {file.type}
                  </li>
                ))
              ) : (
                <div className='text-center italic text-gray-600'>
                  Загрузите файлы
                </div>
              )}
            </ul>
          </div>
          <div className='grid grid-cols-3'>
            {/* <div className=' box-border h-64 w-36 border bg-slate-600'></div>
<div className=' box-border h-64 w-36 border bg-slate-600'></div>
<div className=' box-border h-64 w-36 border bg-slate-600'></div>
<div className=' box-border h-64 w-36 border bg-slate-600'></div> */}
          </div>
          <div className='flex flex-row justify-end px-4'>
            <Button
              className='my-2'
              loading={aspLoading}
              disabled={
                isASPDisabled(student?.school?.school_id) ||
                uploadDisabled ||
                files?.length === 0
              }
              Icon={ArrowUpTrayIcon}
              text='Загрузить документы'
              onClick={() => handleUploadClick()}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
